import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { sessionActions } from '../../../../store';
import { history } from '../../../../helpers';
import { useEvvQrDialogPoll } from '../../hooks';
import { buildEvvVerificationRoute, createEvvToken, isMobileChrome } from '../../utils';

import type { EVVTokenKind, IEVVSessionStatus } from '../../../../utils/types';
import type {
  EvvQrDialogState,
  StartEvvVerificationParams,
  UseEvvVerificationParams,
  UseEvvVerificationResult,
} from './types';

export function useEvvVerification({
  sessionId,
}: UseEvvVerificationParams): UseEvvVerificationResult {
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const onCapturedRef = useRef<(() => void) | null>(null);

  const [evvStatus, setEvvStatus] = useState<IEVVSessionStatus | null>(null);
  const [evvLoadingKind, setEvvLoadingKind] = useState<EVVTokenKind | null>(null);
  const [evvQrDialog, setEvvQrDialog] = useState<EvvQrDialogState | null>(null);

  const refreshEvvStatus = useCallback(async (sessionIdOverride?: number | string) => {
    const evvSessionId = sessionIdOverride ?? sessionId;
    if (!evvSessionId) return null;

    const response = await dispatch(
      sessionActions.getEvvStatus({ sessionId: evvSessionId, getAccessTokenSilently }),
    );

    if (response.error) return null;

    const payload: IEVVSessionStatus = response.payload;
    setEvvStatus(payload);
    return payload;
  }, [dispatch, getAccessTokenSilently, sessionId]);

  const closeEvvQrDialog = useCallback(() => {
    setEvvQrDialog(null);
    onCapturedRef.current = null;
  }, []);

  const startEvvVerification = useCallback(
    async ({
      kind,
      sessionIdOverride,
      replaceActive,
      suppressError,
      onCaptured,
    }: StartEvvVerificationParams) => {
      const evvSessionId = sessionIdOverride ?? sessionId;
      if (!evvSessionId) return { started: false };

      setEvvLoadingKind(kind);
      const response = await dispatch(
        createEvvToken({ kind, sessionId: evvSessionId, replaceActive, getAccessTokenSilently }),
      );
      setEvvLoadingKind(null);

      if (response.error) {
        if (!suppressError?.(response.error)) {
          toast.error(response.error.message);
        }
        return { started: false, error: response.error };
      }

      const route = buildEvvVerificationRoute({
        token: response.payload.token,
        kind,
        sessionId: evvSessionId,
        returnToSession: isMobileChrome(),
      });
      onCapturedRef.current = onCaptured ?? null;

      if (isMobileChrome()) {
        history.navigate(route);
        return { started: true, redirected: true, route, sessionId: evvSessionId };
      }

      setEvvQrDialog({ kind, route, sessionId: evvSessionId });
      return { started: true, redirected: false, route, sessionId: evvSessionId };
    },
    [dispatch, getAccessTokenSilently, sessionId],
  );

  useEffect(() => {
    void refreshEvvStatus();
  }, [refreshEvvStatus]);

  useEvvQrDialogPoll({
    dialog: evvQrDialog,
    onStatus: setEvvStatus,
    onTokenRefreshed: (route) => setEvvQrDialog((dialog) => dialog && { ...dialog, route }),
    onCaptured: () => {
      const onCaptured = onCapturedRef.current;
      setEvvQrDialog(null);
      onCapturedRef.current = null;
      onCaptured?.();
    },
  });

  return {
    evvStatus,
    evvLoadingKind,
    evvQrDialog,
    closeEvvQrDialog,
    refreshEvvStatus,
    startEvvVerification,
  };
}
